import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api } from "../lib/api";

const PROJECT_TYPES = ["Hospitality", "Luxury Residential", "Commercial", "Not sure yet"];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", project_type: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Name, email and message are required.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      toast.success("Your note has arrived at the studio.");
      setForm({ name: "", email: "", phone: "", project_type: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="bg-bone" data-testid="contact-page">
      <section className="pt-40 md:pt-56 pb-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-6">
          <div className="col-span-12 md:col-span-7">
            <p className="text-[11px] uppercase tracking-[0.3em] text-muteInk mb-6">— Start a project</p>
            <h1 className="font-serif text-[12vw] md:text-[7.5vw] leading-[0.9] tracking-tight text-ink">
              Tell us the<br /><span className="italic text-clay">first line.</span>
            </h1>
            <p className="mt-10 max-w-md text-ink/75 leading-relaxed">
              We take on a small number of new projects each year. Share a few honest sentences and we will be in touch within three working days.
            </p>
          </div>
          <div className="col-span-12 md:col-span-5 md:pt-6 text-[13px] leading-relaxed text-ink/75">
            <p className="text-[11px] uppercase tracking-[0.28em] text-muteInk">Studio</p>
            <p className="mt-3">
              13, 4th Main Road<br />
              Indiranagar 2nd Stage<br />
              Bangalore · India<br />
              studio@musenaura.com<br />
              +91 63663 02902
            </p>
            <p className="text-[11px] uppercase tracking-[0.28em] text-muteInk mt-8">Hours</p>
            <p className="mt-3">Mon — Sat · 10:30 to 19:00 IST</p>
          </div>
        </div>
      </section>

      <section className="pb-32 md:pb-40">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <motion.form
            onSubmit={submit}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="border-t border-borderWarm pt-16 grid grid-cols-12 gap-6 md:gap-10"
            data-testid="contact-form"
          >
            {sent && (
              <div className="col-span-12 border border-ink/20 bg-stone2/40 p-6">
                <p className="text-[11px] uppercase tracking-[0.28em] text-clay">— Received</p>
                <p className="font-serif text-2xl md:text-3xl text-ink mt-3 leading-tight">
                  Thank you. Your note has reached us. We will write back within three working days.
                </p>
              </div>
            )}
            <Field label="Your name" value={form.name} onChange={set("name")} testid="contact-name" required />
            <Field label="Email" type="email" value={form.email} onChange={set("email")} testid="contact-email" required />
            <Field label="Phone" value={form.phone} onChange={set("phone")} testid="contact-phone" />
            <div className="col-span-12 md:col-span-6">
              <label className="block text-[10px] uppercase tracking-[0.3em] text-muteInk mb-3">Project type</label>
              <div className="flex flex-wrap gap-2">
                {PROJECT_TYPES.map((t) => (
                  <button
                    type="button"
                    key={t}
                    onClick={() => setForm({ ...form, project_type: t })}
                    data-testid={`contact-type-${t.toLowerCase().replace(/\s+/g, "-")}`}
                    className={`px-4 py-2.5 text-[11px] uppercase tracking-[0.22em] border transition-colors duration-300 ${
                      form.project_type === t ? "bg-ink text-bone border-ink" : "border-borderWarm text-ink hover:border-ink"
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>
            <div className="col-span-12">
              <label className="block text-[10px] uppercase tracking-[0.3em] text-muteInk mb-3">Your project, in your words</label>
              <textarea
                value={form.message}
                onChange={set("message")}
                data-testid="contact-message"
                rows={6}
                required
                className="w-full bg-transparent border-b border-borderWarm focus:border-ink outline-none py-3 font-serif text-2xl md:text-3xl text-ink placeholder-muteInk/40 tracking-tight leading-snug transition-colors duration-300"
                placeholder="A boutique hotel in Sri Lanka. Fifty rooms. A slow arrival…"
              />
            </div>
            <div className="col-span-12 flex items-center justify-between pt-4">
              <p className="text-[11px] uppercase tracking-[0.28em] text-muteInk">— We reply within 3 working days</p>
              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit"
                className="inline-flex items-center gap-3 border border-ink px-8 py-3.5 text-[12px] uppercase tracking-[0.22em] hover:bg-ink hover:text-bone transition-colors duration-300 disabled:opacity-50"
              >
                {loading ? "Sending…" : "Send note"} <span className="text-clay">→</span>
              </button>
            </div>
          </motion.form>
        </div>
      </section>
    </main>
  );
}

function Field({ label, value, onChange, type = "text", testid, required }) {
  return (
    <div className="col-span-12 md:col-span-6">
      <label className="block text-[10px] uppercase tracking-[0.3em] text-muteInk mb-3">{label}</label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        data-testid={testid}
        className="w-full bg-transparent border-b border-borderWarm focus:border-ink outline-none py-3 font-serif text-2xl md:text-3xl text-ink placeholder-muteInk/40 tracking-tight transition-colors duration-300"
      />
    </div>
  );
}
